/**
 * Task detail — the workbench, per V3 R3 and V2 §8.1.
 *
 * Everything about a task is editable here: description, people, dates,
 * dependencies, checklist, subtasks, attachments and comments, with a merged
 * activity timeline.
 *
 * Permissions are checked twice by design. `useCan` hides what the role can
 * never do, and the server re-checks every write — so a control that *is*
 * shown may still be refused (a manager out of scope, for instance), which
 * surfaces as a toast rather than a silent no-op.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import {
  ArrowLeft,
  Copy,
  Eye,
  EyeOff,
  Loader2,
  Play,
  Square,
  Trash2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { PERMISSIONS } from '@virtuetasks/types';
import { PageHeader } from '@/app/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { RichTextEditor, type RichTextEditorHandle } from '@/components/shared/RichTextEditor';
import { ApiError } from '@/lib/api';
import { useAuth, useCan } from '@/features/auth';
import { useUsers } from '@/features/settings/api';
import { UserLink } from '@/components/shared/UserLink';
import { useProject } from '@/features/projects';
import { useRunningTimer, useStartTimer, useStopTimer } from '@/features/time';
import { useProjectSprints } from '@/features/views';
import { StatusBanner } from './StatusBanner';
import { useRoom } from '@/hooks/useRealtime';
import { CommentThread } from './CommentThread';
import { AttachmentSection } from './AttachmentSection';
import {
  DateField,
  EstimateField,
  LabelPicker,
  PeoplePicker,
  PRIORITY_COLOUR,
  PrioritySelect,
  StatusSelect,
  TaskPicker,
  TypeSelect,
} from './fields';
import {
  useChangeStatus,
  useChecklist,
  useDeleteTask,
  useDependencies,
  useSetDependency,
  useSubtasks,
  useTask,
  useTimeline,
  useUpdateTask,
  useWatch,
  type TaskView,
} from './api';

const DESCRIPTION_AUTOSAVE_MS = 2000;

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[92px_1fr] items-start gap-2 py-1">
      <span className="pt-1.5 text-[12px] vt-muted">{label}</span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/** Renders one activity row in plain English. */
function activityLine(payload: Record<string, unknown>): string {
  const action = String(payload.action ?? '');
  const changes = (payload.changes ?? []) as { field: string }[];

  // V6-02: whose comment was deleted is the whole point of the row. "deleted a
  // comment" with no owner reads as self-tidying even when it was moderation.
  if (action === 'comment.deleted') {
    const meta = (payload.metadata ?? {}) as { moderated?: boolean; commentAuthorName?: string };
    return meta.moderated
      ? `deleted a comment by ${meta.commentAuthorName ?? 'someone'}`
      : 'deleted their comment';
  }

  const verb = action.replace(/^task\./, '').replace(/_/g, ' ');
  if (changes.length > 0) return `${verb} — ${changes.map((c) => c.field).join(', ')}`;
  return verb;
}

export function TaskDetailPage() {
  const { key = '' } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const can = useCan();
  const { data: allPeople = [] } = useUsers();
  const peopleById = useMemo(
    () => Object.fromEntries(allPeople.map((p) => [p._id, { name: p.name, avatarUrl: p.avatarUrl }])),
    [allPeople],
  );

  const { data: task, isLoading } = useTask(key);
  const { data: project } = useProject(task?.projectId);
  const { data: subtasks = [] } = useSubtasks(task?._id);
  const { data: deps } = useDependencies(task?._id);
  const { data: timeline = [] } = useTimeline(key);
  const { data: sprints = [] } = useProjectSprints(task?.projectId);

  const update = useUpdateTask();
  const changeStatus = useChangeStatus();
  const watch = useWatch(task?._id ?? '');
  const remove = useDeleteTask();
  const setDependency = useSetDependency(task?._id ?? '');
  const checklist = useChecklist(task?._id ?? '');
  const { data: running } = useRunningTimer();
  const startTimer = useStartTimer();
  const stopTimer = useStopTimer();

  const editorRef = useRef<RichTextEditorHandle>(null);
  const pendingDescription = useRef<Record<string, unknown> | null>(null);
  const [title, setTitle] = useState('');
  const [titleDirty, setTitleDirty] = useState(false);
  const [descDirty, setDescDirty] = useState(false);
  const [checklistText, setChecklistText] = useState('');
  const [commentsOnly, setCommentsOnly] = useState(false);

  const isWatching = (task?.watcherIds ?? []).includes(user?._id ?? '');

  // Live updates for anyone else looking at this task or project (§F15).
  useRoom(task ? `task:${task._id}` : undefined);
  useRoom(task ? `project:${task.projectId}` : undefined);

  useEffect(() => {
    if (task) setTitle(task.title);
  }, [task?._id, task?.title]);

  /** Every write goes through here so a refusal is always explained. */
  async function patch(body: Record<string, unknown>): Promise<void> {
    if (!task) return;
    try {
      await update.mutateAsync({ id: task._id, version: task.version, patch: body });
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : 'Could not save — someone may have edited first',
      );
    }
  }

  // Description autosaves on idle, like the note editor.
  useEffect(() => {
    if (!descDirty) return;
    const timer = setTimeout(() => {
      const doc = pendingDescription.current;
      if (doc) void patch({ description: doc });
      setDescDirty(false);
    }, DESCRIPTION_AUTOSAVE_MS);
    return () => clearTimeout(timer);
  }, [descDirty]);

  // §8.5 detail shortcuts: M toggles watch, ⌘⇧C copies the key + link.
  useEffect(() => {
    if (!task) return;
    const onKey = (e: KeyboardEvent): void => {
      const target = e.target as HTMLElement | null;
      if (target?.closest('input, textarea, [contenteditable="true"]')) return;
      if (e.key.toLowerCase() === 'm' && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        watch.mutate(isWatching);
      }
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'c') {
        e.preventDefault();
        void navigator.clipboard.writeText(`${task.key} ${window.location.href}`);
        toast.success('Key and link copied');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (isLoading) {
    return (
      <div className="p-4">
        <Skeleton className="mb-3 h-8 w-1/2" />
        <Skeleton className="h-64" />
      </div>
    );
  }
  if (!task) {
    return (
      <div className="p-6 text-[13px] vt-muted">That task does not exist, or you cannot see it.</div>
    );
  }

  const timerOnThis = running?.taskId === task._id;
  const canEdit = can(PERMISSIONS.TASK_UPDATE);
  const statuses = project?.statuses ?? [];
  const currentStatus = statuses.find((s) => s.id === task.statusId);
  // Reopening lands on the first non-terminal status — the project's own
  // backlog column, whatever it is called here.
  const reopenStatusId = statuses.find((s) => s.category === 'todo')?.id;
  const canAssign = can(PERMISSIONS.TASK_ASSIGN);
  const rows = commentsOnly ? timeline.filter((r) => r.type === 'comment') : timeline;

  return (
    <>
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <span
              className="h-5 w-[3px] rounded-full"
              style={{ background: PRIORITY_COLOUR[task.priority] }}
              aria-hidden
            />
            <Link
              to={`/projects/${task.projectKey}/board`}
              className="vt-mono text-[13px] vt-muted hover:underline"
            >
              {task.key}
            </Link>
          </span>
        }
        subtitle={project?.name}
        actions={
          <>
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft />
              Back
            </Button>
            <Button
              variant="ghost"
              size="sm"
              data-testid="watch-toggle"
              onClick={() => watch.mutate(isWatching)}
            >
              {isWatching ? <EyeOff /> : <Eye />}
              {isWatching ? 'Unwatch' : 'Watch'}
            </Button>
            <Button
              variant={timerOnThis ? 'subtle' : 'ghost'}
              size="sm"
              data-testid="timer-toggle"
              onClick={async () => {
                if (timerOnThis) {
                  await stopTimer.mutateAsync();
                  toast.success('Timer stopped');
                } else {
                  await startTimer.mutateAsync(task._id);
                  toast.success('Timer started');
                }
              }}
            >
              {timerOnThis ? <Square /> : <Play />}
              {timerOnThis ? 'Stop' : 'Start'}
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Copy key and link"
              onClick={() => {
                void navigator.clipboard.writeText(`${task.key} ${window.location.href}`);
                toast.success('Key and link copied');
              }}
            >
              <Copy />
            </Button>
            {can(PERMISSIONS.TASK_DELETE) && (
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Delete task"
                onClick={async () => {
                  if (!window.confirm(`Delete ${task.key}?`)) return;
                  try {
                    await remove.mutateAsync(task._id);
                    toast.success(`${task.key} deleted`);
                    navigate(`/projects/${task.projectKey}/board`);
                  } catch (err) {
                    toast.error(err instanceof ApiError ? err.message : 'Could not delete');
                  }
                }}
              >
                <Trash2 />
              </Button>
            )}
          </>
        }
      />

      {/* V4 D-2: a terminal state announces itself before anything else on
          the page is read. Full width, above the title, outside the grid. */}
      <div className="px-4 pt-4">
        <StatusBanner
          category={task.statusCategory}
          statusName={currentStatus?.name ?? task.statusCategory}
          completedAt={task.completedAt}
          blockedReason={task.blockedReason}
          timeline={timeline}
          canReopen={canEdit && Boolean(reopenStatusId)}
          onReopen={() => {
            if (reopenStatusId) void patch({ statusId: reopenStatusId });
          }}
        />
      </div>

      <div className="grid gap-4 p-4 lg:grid-cols-[1fr_300px]">
        {/* ── Main column ── */}
        <div className="flex min-w-0 flex-col gap-5">
          <div>
            <Input
              aria-label="Task title"
              data-testid="task-title"
              className="h-10 text-[18px] font-medium"
              value={title}
              disabled={!canEdit}
              onChange={(e) => {
                setTitle(e.target.value);
                setTitleDirty(true);
              }}
              onBlur={() => {
                if (!titleDirty || title.trim() === task.title) return;
                setTitleDirty(false);
                void patch({ title: title.trim() });
              }}
            />
          </div>

          <section>
            <h3 className="mb-1.5 text-[13px] font-medium">Description</h3>
            <RichTextEditor
              handleRef={editorRef}
              data-testid="task-description"
              value={task.description}
              editable={canEdit}
              minHeight={140}
              placeholder="Context, acceptance criteria, links."
              onChange={(doc) => {
                pendingDescription.current = doc;
                setDescDirty(true);
              }}
            />
            {descDirty && (
              <p className="mt-1 flex items-center gap-1 text-[11px] vt-muted">
                <Loader2 className="size-3 animate-spin" /> Saving…
              </p>
            )}
          </section>

          <section data-testid="checklist">
            <h3 className="mb-1.5 text-[13px] font-medium">
              Checklist{' '}
              {task.checklist.length > 0 && (
                <span className="vt-muted">
                  ({task.checklist.filter((c) => c.done).length}/{task.checklist.length})
                </span>
              )}
            </h3>
            <ul className="flex flex-col gap-1">
              {task.checklist.map((item) => (
                <li key={item.id} className="flex items-center gap-2 text-[13px]">
                  <input
                    type="checkbox"
                    checked={item.done}
                    disabled={!canEdit}
                    aria-label={item.text}
                    onChange={() =>
                      checklist.update.mutate({ itemId: item.id, patch: { done: !item.done } })
                    }
                  />
                  <span className={item.done ? 'flex-1 line-through vt-muted' : 'flex-1'}>
                    {item.text}
                  </span>
                  {canEdit && (
                    <>
                      <button
                        type="button"
                        title="Convert to subtask"
                        aria-label={`Convert ${item.text} to a subtask`}
                        onClick={() => {
                          checklist.convert.mutate(item.id);
                          toast.success('Converted to a subtask');
                        }}
                        className="rounded-[4px] px-1.5 py-0.5 text-[11px] vt-muted hover:bg-[var(--surface-2)]"
                      >
                        → subtask
                      </button>
                      <button
                        type="button"
                        aria-label={`Remove ${item.text}`}
                        onClick={() => checklist.remove.mutate(item.id)}
                        className="grid size-5 place-items-center rounded-[4px] vt-muted hover:bg-[var(--surface-2)]"
                      >
                        <X className="size-3" />
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>
            {canEdit && (
              <Input
                className="mt-1.5 h-7"
                aria-label="Add a checklist item"
                data-testid="checklist-input"
                placeholder="Add a step and press Enter"
                value={checklistText}
                onChange={(e) => setChecklistText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter' || !checklistText.trim()) return;
                  e.preventDefault();
                  checklist.add.mutate(checklistText.trim());
                  setChecklistText('');
                }}
              />
            )}
          </section>

          {subtasks.length > 0 && (
            <section data-testid="subtasks">
              <h3 className="mb-1.5 text-[13px] font-medium">
                Subtasks{' '}
                <span className="vt-muted">
                  ({task.subtaskDoneCount}/{task.subtaskCount})
                </span>
              </h3>
              <ul className="vt-surface overflow-hidden">
                {subtasks.map((sub: TaskView) => (
                  <li key={sub._id}>
                    <Link
                      to={`/tasks/${sub.key}`}
                      className="flex items-center gap-2 border-b border-[var(--border)] px-2.5 py-1.5 text-[13px] last:border-0 hover:bg-[var(--surface-2)]"
                    >
                      <span className="vt-mono text-[11px] vt-muted">{sub.key}</span>
                      <span className="flex-1 truncate">{sub.title}</span>
                      <Badge tone="neutral">{sub.statusCategory.replace('_', ' ')}</Badge>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <AttachmentSection entityId={task._id} entityType="task" readOnly={!canEdit} />

          <CommentThread taskId={task._id} />

          <section data-testid="activity-timeline">
            <div className="mb-1.5 flex items-center gap-2">
              <h3 className="flex-1 text-[13px] font-medium">Activity</h3>
              <label className="flex items-center gap-1.5 text-[12px] vt-muted">
                <input
                  type="checkbox"
                  checked={commentsOnly}
                  onChange={(e) => setCommentsOnly(e.target.checked)}
                />
                Comments only
              </label>
            </div>
            <ul className="flex flex-col gap-1">
              {rows.map((row, i) => (
                <li key={i} className="flex items-baseline gap-2 text-[12px]">
                  <span className="vt-mono shrink-0 vt-muted">
                    {new Date(row.at).toLocaleString()}
                  </span>
                  <span className="font-medium">{String(row.payload.actorName ?? '')}</span>
                  <span className="vt-muted">
                    {row.type === 'comment' ? 'commented' : activityLine(row.payload)}
                  </span>
                </li>
              ))}
              {rows.length === 0 && <li className="text-[12px] vt-muted">Nothing yet.</li>}
            </ul>
          </section>
        </div>

        {/* ── Side panel ── */}
        <aside className="flex flex-col gap-1 lg:border-l lg:border-[var(--border)] lg:pl-4">
          <Field label="Status">
            <StatusSelect
              statuses={project?.statuses ?? []}
              value={task.statusId}
              disabled={!can(PERMISSIONS.TASK_CHANGE_STATUS)}
              onChange={async (statusId, reason) => {
                try {
                  await changeStatus.mutateAsync({ id: task._id, statusId, reason });
                } catch (err) {
                  toast.error(err instanceof ApiError ? err.message : 'Could not change the status');
                }
              }}
            />
          </Field>

          <Field label="Priority">
            <PrioritySelect
              value={task.priority}
              disabled={!canEdit}
              onChange={(p) => void patch({ priority: p })}
            />
          </Field>

          <Field label="Type">
            <TypeSelect value={task.type} disabled={!canEdit} onChange={(t) => void patch({ type: t })} />
          </Field>

          <Field label="Assignees">
            <PeoplePicker
              label="Assignees"
              testId="task-assignees"
              value={task.assigneeIds}
              disabled={!canAssign}
              suggestFor={{ projectId: task.projectId, type: task.type }}
              onChange={(ids) => void patch({ assigneeIds: ids })}
            />
            {/* V5-B0: a way through to each assignee's profile. Rendered
                beneath the picker rather than inside it — the picker's trigger
                is a button, and a link nested in a button is neither valid nor
                clickable. */}
            {task.assigneeIds.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-x-2 gap-y-1" data-testid="assignee-links">
                {task.assigneeIds.map((id) => (
                  <UserLink
                    key={id}
                    userId={id}
                    name={peopleById[id]?.name ?? 'Unknown'}
                    avatarUrl={peopleById[id]?.avatarUrl}
                    size={16}
                    className="text-[11px] vt-muted"
                  />
                ))}
              </div>
            )}
          </Field>

          <Field label="Reviewer">
            <PeoplePicker
              label="Reviewer"
              multiple={false}
              value={task.reviewerId ? [task.reviewerId] : []}
              disabled={!canEdit}
              onChange={(ids) => void patch({ reviewerId: ids[0] ?? null })}
            />
          </Field>

          <Field label="Watchers">
            <PeoplePicker
              label="Watchers"
              value={task.watcherIds ?? []}
              disabled={!canEdit}
              onChange={(ids) => void patch({ watcherIds: ids })}
            />
          </Field>

          <Field label="Sprint">
            <select
              aria-label="Sprint"
              data-testid="task-sprint"
              disabled={!canEdit}
              value={task.sprintId ?? ''}
              onChange={(e) => void patch({ sprintId: e.target.value || null })}
              className="h-8 w-full rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-2 text-[13px]"
            >
              <option value="">Backlog</option>
              {sprints
                .filter((sprint) => sprint.status !== 'completed' || sprint._id === task.sprintId)
                .map((sprint) => (
                  <option key={sprint._id} value={sprint._id}>
                    {sprint.name}
                    {sprint.status === 'active' ? ' (active)' : ''}
                  </option>
                ))}
            </select>
          </Field>

          <Field label="Labels">
            <LabelPicker
              value={task.labelIds}
              disabled={!canEdit}
              onChange={(ids) => void patch({ labelIds: ids })}
            />
          </Field>

          <Field label="Start">
            <DateField
              label="Start date"
              value={task.startDate}
              disabled={!canEdit}
              onChange={(v) => void patch({ startDate: v })}
            />
          </Field>

          <Field label="Due">
            <DateField
              label="Due date"
              testId="task-due"
              value={task.dueDate}
              disabled={!canEdit}
              onChange={(v) => void patch({ dueDate: v })}
            />
          </Field>

          <Field label="Estimate">
            <EstimateField
              minutes={task.estimateMinutes}
              disabled={!canEdit}
              onChange={(m) => void patch({ estimateMinutes: m })}
            />
          </Field>

          <Field label="Spent">
            <span className="text-[13px]">
              {task.spentMinutes ? `${(task.spentMinutes / 60).toFixed(1)}h` : '—'}
              {task.estimateMinutes ? (
                <span className="vt-muted"> of {(task.estimateMinutes / 60).toFixed(1)}h</span>
              ) : null}
            </span>
          </Field>

          <Field label="Blocked by">
            <div className="flex flex-wrap items-center gap-1">
              {(deps?.blockedBy ?? []).map((t) => (
                <span
                  key={t._id}
                  className="vt-mono flex items-center gap-1 rounded-[4px] bg-[var(--surface-2)] px-1.5 py-0.5 text-[11px]"
                >
                  <Link to={`/tasks/${t.key}`} className="hover:underline">
                    {t.key}
                  </Link>
                  {canEdit && (
                    <button
                      type="button"
                      aria-label={`Remove ${t.key}`}
                      onClick={() =>
                        setDependency.mutate({ taskId: t._id, relation: 'blockedBy', remove: true })
                      }
                    >
                      <X className="size-3" />
                    </button>
                  )}
                </span>
              ))}
              {canEdit && (
                <TaskPicker
                  label="+ Add"
                  testId="add-blocked-by"
                  projectId={task.projectId}
                  excludeId={task._id}
                  onPick={async (t) => {
                    try {
                      await setDependency.mutateAsync({ taskId: t._id, relation: 'blockedBy' });
                    } catch (err) {
                      // A cycle is the interesting failure — surface the server's words.
                      toast.error(err instanceof ApiError ? err.message : 'Could not add the dependency');
                    }
                  }}
                />
              )}
            </div>
          </Field>

          <Field label="Blocks">
            <div className="flex flex-wrap items-center gap-1">
              {(deps?.blocks ?? []).map((t) => (
                <span
                  key={t._id}
                  className="vt-mono flex items-center gap-1 rounded-[4px] bg-[var(--surface-2)] px-1.5 py-0.5 text-[11px]"
                >
                  <Link to={`/tasks/${t.key}`} className="hover:underline">
                    {t.key}
                  </Link>
                  {canEdit && (
                    <button
                      type="button"
                      aria-label={`Remove ${t.key}`}
                      onClick={() =>
                        setDependency.mutate({ taskId: t._id, relation: 'blocks', remove: true })
                      }
                    >
                      <X className="size-3" />
                    </button>
                  )}
                </span>
              ))}
              {canEdit && (
                <TaskPicker
                  label="+ Add"
                  projectId={task.projectId}
                  excludeId={task._id}
                  onPick={async (t) => {
                    try {
                      await setDependency.mutateAsync({ taskId: t._id, relation: 'blocks' });
                    } catch (err) {
                      toast.error(err instanceof ApiError ? err.message : 'Could not add the dependency');
                    }
                  }}
                />
              )}
            </div>
          </Field>

          <div className="mt-2 border-t border-[var(--border)] pt-2 text-[11px] vt-muted">
            <div>Created {new Date(task.createdAt).toLocaleString()}</div>
            <div>Updated {new Date(task.updatedAt).toLocaleString()}</div>
          </div>
        </aside>
      </div>
    </>
  );
}
